import { useContext, useState } from 'react'
import { EditorModeContext } from '../EditorModeContext'
import usePointerWithin from './usePointerWithin'

interface UseEmbedControlsOptions {
  savedHeight: number
  entityKey?: string
  // The embed has an Edit dialog it can open (and whatever that needs, such as an entity key).
  editable: boolean
  onResizeHeight?: (entityKey: string, height: number) => void
  // How to tell the pointer is over the embed. Tables and dashboards have pointer events off in edit
  // mode (style.scss), so they're followed at document level ('pointer'). An iframe keeps pointer
  // events, and moves over it go to the embedded page instead, so it relies on enter/leave.
  hoverTracking?: 'pointer' | 'enterLeave'
}

/**
 * The edit-mode controls shared by embedded tables, dashboards and iframes: the hover Edit button and
 * the drag-to-resize handle. Spread `viewProps` onto the embed's `relative` box, size it to `height`,
 * render the Edit button while `showEditButton`, and an EmbedResizeHandle with `resizeHandleProps`
 * while `canResize`.
 */
const useEmbedControls = ({
  savedHeight,
  entityKey,
  editable,
  onResizeHeight,
  hoverTracking = 'pointer'
}: UseEmbedControlsOptions) => {
  const { readOnly } = useContext(EditorModeContext)
  const [dragHeight, setDragHeight] = useState<number>()
  // The rendered embed (state, so the pointer tracking re-binds once it renders).
  const [viewEl, setViewEl] = useState<HTMLElement | null>(null)
  const [entered, setEntered] = useState(false)

  // Both are offered whenever the document is being edited, while the pointer is over the embed.
  const canEdit = editable && !readOnly
  const canResize = !readOnly && !!entityKey && !!onResizeHeight
  const pointerWithin = usePointerWithin(viewEl, hoverTracking === 'pointer' && (canEdit || canResize))
  const viewHovered = hoverTracking === 'pointer' ? pointerWithin : entered
  const dragging = dragHeight !== undefined

  return {
    height: dragHeight ?? savedHeight,
    dragging,
    showEditButton: canEdit && viewHovered && !dragging,
    canResize,
    viewProps: {
      ref: setViewEl,
      // The box the resize edge sits on, which the selected-insert outline is drawn around
      'data-embed-view': '',
      onMouseEnter: () => setEntered(true),
      onMouseLeave: () => setEntered(false),
      // Keys pressed inside the embed (its Edit button, a cell editor) belong to what's inside. Stop them here, after
      // they've been handled there and before the document editor, which would otherwise act on the insert its
      // cursor is on: Enter would add a line, Backspace/Delete remove the insert, arrows move around it.
      onKeyDown: readOnly ? undefined : (event: React.KeyboardEvent) => event.stopPropagation()
    },
    resizeHandleProps: {
      height: savedHeight,
      viewHovered,
      onResize: setDragHeight,
      onResizeEnd: (newHeight: number) => {
        setDragHeight(undefined)
        if (entityKey && newHeight !== savedHeight) onResizeHeight?.(entityKey, newHeight)
      }
    }
  }
}

export default useEmbedControls
